import { z } from 'zod';
import { zDateRequired, zStringRequired } from '../z';
import { refineSchema } from './refine-schema';
import { sectionAConsistency } from './oasis-rules/consistency/section-a';
import { sectionCConsistency } from './oasis-rules/consistency/section-c';
import { sectionDConsistency } from './oasis-rules/consistency/section-d';
import { sectionEConsistency } from './oasis-rules/consistency/section-e';
import { sectionFConsistency } from './oasis-rules/consistency/section-f';
import { sectionGGConsistency } from './oasis-rules/consistency/section-gg';
import { sectionHConsistency } from './oasis-rules/consistency/section-h';
import { sectionIConsistency } from './oasis-rules/consistency/section-i';
import { sectionJConsistency } from './oasis-rules/consistency/section-j';
import { sectionKConsistency } from './oasis-rules/consistency/section-k';
import { sectionMConsistency } from './oasis-rules/consistency/section-m';
import { sectionNConsistency } from './oasis-rules/consistency/section-n';
import { sectionOConsistency } from './oasis-rules/consistency/section-o';
import { sectionASkipPatterns } from './oasis-rules/skip-pattern/section-a';
import { sectionCSkipPatterns } from './oasis-rules/skip-pattern/section-c';
import { sectionGGSkipPatterns } from './oasis-rules/skip-pattern/section-gg';
import { sectionJSkipPatterns } from './oasis-rules/skip-pattern/section-j';
import { sectionMSkipPatterns } from './oasis-rules/skip-pattern/section-m';
import { sectionNSkipPatterns } from './oasis-rules/skip-pattern/section-n';
import { sectionOSkipPatterns } from './oasis-rules/skip-pattern/section-o';
import { RespiratoryVersioned } from './oasis-sections/respiratory';
import { LabDrawVersioned } from './oasis-sections/labDraw';
import { baseLungAuscultationSchema, baseLungAuscultationSchemaRefined, baseBreathingPatternSchema, baseCpapBipapSchema, baseMechanicalVentilatorSchema, baseTracheostomySchema, baseRespiratoryTreatmentsSchema, baseSmokingHistorySchema, baseCoughSchema, } from './shared/respiratory';
import { baseLabDrawSchema } from './shared/labDraw';
import { ssnSchema } from './shared/ssn';
import { CardiovascularVersioned } from './oasis-sections/cardiovascular';
import { baseCardiovascularSchema, baseCardiovascularSchemaRefined } from './shared/cardiovascular';
import { OstomyVersioned } from './oasis-sections/ostomy';
import { baseOstomySchema, baseOstomySchemaRefined } from './shared/ostomy';
import { EndocrineVersioned } from './oasis-sections/endocrine';
import { baseEndocrineSchema } from './shared/endocrine';
import { HealthManagementVersioned } from './oasis-sections/health-management';
import { baseHealthManagementSchema } from './shared/health-management';
import { CoordinationOfCareVersioned } from './oasis-sections/coordination-of-care';
import { NarrativeVersioned } from './oasis-sections/narrative';
import { PlanOfCareVersioned } from './oasis-sections/plan-of-care';
import { processedA1005, processedA1010, processedA1250, processedM0066, processedM0150, processedM1000, processedM2310, SectionAVersioned, } from './oasis-sections/section-a';
import { baseMouthNoseThroat, baseMouthNoseThroatRefined, SectionBVersioned } from './oasis-sections/section-b';
import { baseC0300, baseC0400, baseC1310, SectionCVersioned } from './oasis-sections/section-c';
import { baseD0150, SectionDVersioned } from './oasis-sections/section-d';
import { basePsychosocialSchema, basePsychosocialSchemaRefined } from './shared/psychosocial';
import { SectionEVersioned } from './oasis-sections/section-e';
import { baseM1100, SectionFVersioned } from './oasis-sections/section-f';
import { baseHomebound, baseHomeboundRefined, HomeboundVersioned } from './oasis-sections/homebound';
import { baseMusculoskeletal, baseMusculoskeletalRefined, SectionGVersioned } from './oasis-sections/section-g';
import { baseGG0100, SectionGGVersioned } from './oasis-sections/section-gg';
import { processedM1028, SectionIVersioned } from './oasis-sections/section-i';
import { baseJ1900, processedM1033, SectionJVersioned } from './oasis-sections/section-j';
import { basePainAssessmentSchema } from './shared/pain-assessment';
import { baseEatingSupport, baseK0520Clinical, baseM1060, baseM1060Refined, baseOralIntakeClinical, basePocNutrition, baseWeightClinical, SectionKVersioned, } from './oasis-sections/section-k';
import { baseIntegumentary, baseIntegumentaryRefined, SectionMVersioned } from './oasis-sections/section-m';
import { baseAbilityAssessment, baseActionsTaken, baseN0415, SectionNVersioned } from './oasis-sections/section-n';
import { baseM2200, baseM2200Refined, baseO0110, SectionOVersioned } from './oasis-sections/section-o';
import { SectionQVersioned } from './oasis-sections/section-q';
import { SuppliesVersioned } from './oasis-sections/supplies';
import { baseBloodPressure, baseBloodPressureRefined, baseBloodSugar, baseBloodSugarRefined, baseO2, baseO2Refined, baseRespiration, baseRespirationRefined, basePulse, basePulseRefined, baseTemperature, baseTemperatureRefined, VitalsVersioned, } from './oasis-sections/vitals';
import { basePain, basePainRefined } from './shared/pain';
import { resolveVersionEntry, } from './oasis-types';
import { MedicationVersioned } from './oasis-sections/medication';
import { baseHearingSchema, baseNeurologicalSchema, baseNeurologicalSchemaRefined, baseVisionSchema, hearingRefined, visionRefined, } from './shared/neurological';
import { SectionHVersioned } from './oasis-sections/section-h';
import { baseGenitourinarySchema, baseGenitourinarySchemaRefined } from './shared/genitourinary';
import { baseGastrointestinalSchema, baseGastrointestinalSchemaRefined } from './shared/gastrointestinal';
// manage which timepoints and versions fields are shown in
const oasis = {
    SectionA: SectionAVersioned,
    SectionB: SectionBVersioned,
    SectionC: SectionCVersioned,
    SectionBC: { ...SectionCVersioned, ...SectionBVersioned },
    SectionD: SectionDVersioned,
    SectionE: SectionEVersioned,
    SectionDE: { ...SectionEVersioned, ...SectionDVersioned },
    SectionF: SectionFVersioned,
    SectionG: SectionGVersioned,
    SectionGG: SectionGGVersioned,
    SectionH: SectionHVersioned,
    SectionI: SectionIVersioned,
    SectionJ: SectionJVersioned,
    SectionK: SectionKVersioned,
    SectionM: SectionMVersioned,
    Medication: MedicationVersioned,
    SectionN: SectionNVersioned,
    SectionO: SectionOVersioned,
    Vitals: VitalsVersioned,
    SectionQ: SectionQVersioned,
    Respiratory: RespiratoryVersioned,
    Cardiovascular: CardiovascularVersioned,
    Endocrine: EndocrineVersioned,
    HealthManagement: HealthManagementVersioned,
    Narrative: NarrativeVersioned,
    CoordinationOfCare: CoordinationOfCareVersioned,
    Supplies: SuppliesVersioned,
    PlanOfCare: PlanOfCareVersioned,
    Homebound: HomeboundVersioned,
    Labdraw: LabDrawVersioned,
    Ostomy: OstomyVersioned,
};
export const baseDiagnosis = z.object({
    diagnosisId: z.string().optional(),
    code: z.string().optional(),
    description: z.string(),
    severity: z.string().nullable().optional(),
    onsetDate: z
        .string()
        .nullish()
        .transform((value) => (value === '' ? null : value))
        .refine((value) => value === null || (value && !isNaN(Date.parse(value))), {
        message: 'Invalid date',
    }),
});
/**
 *
 * @param version
 * @param timepoint
 * @returns dynamic validation schema based on oasis timepoint and version
 */
export const getBaseOasisValidationSchema = (version, timepoint) => {
    const isRequired = (section, { isSection = false, item, subItem }) => {
        if (!isSection && item) {
            const itemConfig = oasis[section]?.[item];
            if (!itemConfig) {
                return false;
            }
            const entry = resolveVersionEntry(itemConfig, version);
            if (!entry) {
                return false;
            }
            if (subItem) {
                const subEntry = entry.fields?.[subItem];
                if (!subEntry) {
                    return false;
                }
                return subEntry.timepoint.includes(timepoint) || subEntry.timepoint.includes('*');
            }
            return entry.timepoint.includes(timepoint) || entry.timepoint.includes('*');
        }
        else {
            for (const itemKey in oasis[section]) {
                const itemConfig = oasis[section][itemKey];
                const entry = resolveVersionEntry(itemConfig, version);
                if (entry && (entry.timepoint.includes(timepoint) || entry.timepoint.includes('*'))) {
                    return true;
                }
            }
            return false;
        }
    };
    const baseM0040 = z.object({
        firstName: zStringRequired(),
        middleName: z.string().optional(),
        lastName: zStringRequired(),
        suffix: z.string().optional(),
    });
    const baseM0050M0060 = z.object({
        m0050: isRequired('SectionA', { item: 'm0050-m0060', subItem: 'm0050' }) ? zStringRequired() : z.string().optional(),
        m0060: isRequired('SectionA', { item: 'm0050-m0060', subItem: 'm0060' }) ? zStringRequired() : z.string().optional(),
    });
    const m0063 = z
        .object({
        m0063: isRequired('SectionA', { item: 'm0063-m0065', subItem: 'm0063' })
            ? z.string().min(1, 'Required')
            : z.string().min(1).optional(),
        m0063NA: isRequired('SectionA', { item: 'm0063-m0065', subItem: 'm0063' }) ? z.boolean().optional() : z.boolean(),
    })
        .or(z.object({
        m0063: isRequired('SectionA', { item: 'm0063-m0065', subItem: 'm0063' }) ? z.string().optional() : z.string().optional(),
        m0063NA: isRequired('SectionA', { item: 'm0063-m0065', subItem: 'm0063' })
            ? z.literal(true, { message: 'Required' })
            : z.literal(true).optional(),
    }));
    const m0064 = z
        .object({
        m0064: isRequired('SectionA', { item: 'm0063-m0065', subItem: 'm0064' }) ? ssnSchema() : ssnSchema().optional(),
        m0064NA: isRequired('SectionA', { item: 'm0063-m0065', subItem: 'm0064' }) ? z.boolean().optional() : z.boolean(),
    })
        .or(z.object({
        m0064: isRequired('SectionA', { item: 'm0063-m0065', subItem: 'm0064' }) ? z.string().optional() : z.string().optional(),
        m0064NA: isRequired('SectionA', { item: 'm0063-m0065', subItem: 'm0064' })
            ? z.literal(true, { message: 'Required' })
            : z.literal(true).optional(),
    }));
    const m0065 = z
        .object({
        m0065: isRequired('SectionA', { item: 'm0063-m0065', subItem: 'm0065' })
            ? z.string().min(1, 'Required')
            : z.string().min(1).optional(),
        m0065NA: isRequired('SectionA', { item: 'm0063-m0065', subItem: 'm0065' }) ? z.boolean().optional() : z.boolean(),
    })
        .or(z.object({
        m0065: isRequired('SectionA', { item: 'm0063-m0065', subItem: 'm0065' }) ? z.string().optional() : z.string().optional(),
        m0065NA: isRequired('SectionA', { item: 'm0063-m0065', subItem: 'm0065' })
            ? z.literal(true, { message: 'Required' })
            : z.literal(true).optional(),
    }));
    const baseM0063M0065 = z.intersection(z.intersection(m0063, m0064), m0065);
    const baseSectionA = z.object({
        m0018: isRequired('SectionA', { item: 'm0018' }) ? z.string().nullable() : z.string().nullish(),
        m0018NA: isRequired('SectionA', { item: 'm0018' }) ? z.boolean() : z.boolean().optional(),
        m0040: isRequired('SectionA', { item: 'm0040' }) ? baseM0040 : baseM0040.optional(),
        'm0050-m0060': isRequired('SectionA', { item: 'm0050-m0060' }) ? baseM0050M0060 : baseM0050M0060.optional(),
        'm0063-m0065': isRequired('SectionA', { item: 'm0063-m0065' }) ? baseM0063M0065 : baseM0063M0065.optional(),
        m0069: isRequired('SectionA', { item: 'm0069' }) ? zStringRequired() : z.string().optional(),
        a0810: isRequired('SectionA', { item: 'a0810' }) ? zStringRequired() : z.string().optional(),
        m0066: isRequired('SectionA', { item: 'm0066' }) ? processedM0066 : processedM0066.optional(),
        a1005: isRequired('SectionA', { item: 'a1005' }) ? processedA1005 : processedA1005.optional(),
        a1010: isRequired('SectionA', { item: 'a1010' }) ? processedA1010 : processedA1010.optional(),
        m0150: isRequired('SectionA', { item: 'm0150' }) ? processedM0150 : processedM0150.optional(),
        m0100: isRequired('SectionA', { item: 'm0100' }) ? zStringRequired() : z.string().optional(),
        m0104: isRequired('SectionA', { item: 'm0104' }) ? z.iso.date().nullable() : z.iso.date().nullish(),
        m0030: isRequired('SectionA', { item: 'm0030' }) ? zDateRequired() : z.iso.date().optional(),
        m0102: isRequired('SectionA', { item: 'm0102' }) ? z.iso.date().nullable() : z.iso.date().nullish(),
        m0102NA: isRequired('SectionA', { item: 'm0102' }) ? z.boolean().optional() : z.boolean().optional(),
        m0090: isRequired('SectionA', { item: 'm0090' }) ? zDateRequired() : z.iso.date().optional(),
        m0080: isRequired('SectionA', { item: 'm0080' }) ? zStringRequired() : z.string().optional(),
        a1255: isRequired('SectionA', { item: 'a1255' }) ? zStringRequired() : z.string().optional(),
        m1000: isRequired('SectionA', { item: 'm1000' }) ? processedM1000 : processedM1000.optional(),
        m1005: isRequired('SectionA', { item: 'm1005' }) ? z.iso.date().nullable() : z.iso.date().nullish(),
        m1005NA: isRequired('SectionA', { item: 'm1005' }) ? z.boolean() : z.boolean().optional(),
        a1110a: isRequired('SectionA', { item: 'a1110' }) ? zStringRequired() : z.string().optional(),
        a1110b: isRequired('SectionA', { item: 'a1110' }) ? zStringRequired() : z.string().optional(),
        a1250: isRequired('SectionA', { item: 'a1250' }) ? processedA1250 : processedA1250.optional(),
        m0032: isRequired('SectionA', { item: 'm0032' }) ? z.iso.date().nullable() : z.iso.date().nullish(),
        m0032NA: isRequired('SectionA', { item: 'm0032' }) ? z.boolean() : z.boolean().optional(),
        m0110: isRequired('SectionA', { item: 'm0110' }) ? zStringRequired() : z.string().optional(),
        m0906: isRequired('SectionA', { item: 'm0906' }) ? z.iso.date().nullable() : z.iso.date().nullish(),
        m2301: isRequired('SectionA', { item: 'm2301' }) ? zStringRequired() : z.string().optional(),
        m2310: isRequired('SectionA', { item: 'm2310' }) ? processedM2310 : processedM2310.optional(),
        m2410: isRequired('SectionA', { item: 'm2410' }) ? zStringRequired() : z.string().optional(),
        m2420: isRequired('SectionA', { item: 'm2420' }) ? zStringRequired() : z.string().optional(),
        a2120: isRequired('SectionA', { item: 'a2120' }) ? zStringRequired() : z.string().optional(),
        a2121: isRequired('SectionA', { item: 'a2121' }) ? z.string().nullable() : z.string().nullish(),
        a2122: isRequired('SectionA', { item: 'a2122' }) ? z.array(z.string()) : z.array(z.string()).optional(),
        a2123: isRequired('SectionA', { item: 'a2123' }) ? z.string().nullable() : z.string().nullish(),
        a2124: isRequired('SectionA', { item: 'a2124' }) ? z.array(z.string()) : z.array(z.string()).optional(),
    });
    const baseSectionAProccessed = refineSchema(baseSectionA, (input, ctx) => {
        sectionAConsistency({ input, ctx, version, isSection: true });
        sectionASkipPatterns({ input, ctx, version, isSection: true });
    });
    const baseSectionB = z.object({
        b0200: isRequired('SectionB', { item: 'b0200' }) ? zStringRequired() : z.string().optional(),
        hearing: isRequired('SectionB', { item: 'hearing' }) ? hearingRefined : baseHearingSchema.optional(),
        b1000: isRequired('SectionB', { item: 'b1000' }) ? zStringRequired() : z.string().optional(),
        vision: isRequired('SectionB', { item: 'vision' }) ? visionRefined : baseVisionSchema.optional(),
        mouthNoseThroat: isRequired('SectionB', { item: 'mouthNoseThroat' }) ? baseMouthNoseThroatRefined : baseMouthNoseThroat.optional(),
        b1300: isRequired('SectionB', { item: 'b1300' }) ? zStringRequired() : z.string().optional(),
    });
    const baseSectionC = z.object({
        c0100: isRequired('SectionC', { item: 'c0100' }) ? zStringRequired() : z.string().optional(),
        c0200: isRequired('SectionC', { item: 'c0200' }) ? z.string().nullable() : z.string().nullish(),
        c0300: isRequired('SectionC', { item: 'c0300' }) ? baseC0300.nullable() : baseC0300.nullish(),
        c0400: isRequired('SectionC', { item: 'c0400' }) ? baseC0400.nullable() : baseC0400.nullish(),
        c0500: isRequired('SectionC', { item: 'c0500' }) ? z.string().nullable() : z.string().nullish(),
        c1310: isRequired('SectionC', { item: 'c1310' }) ? baseC1310 : baseC1310.optional(),
        m1700: isRequired('SectionC', { item: 'm1700' }) ? zStringRequired() : z.string().optional(),
        m1710: isRequired('SectionC', { item: 'm1710' }) ? zStringRequired() : z.string().optional(),
        m1720: isRequired('SectionC', { item: 'm1720' }) ? zStringRequired() : z.string().optional(),
        neurological: isRequired('SectionB', { item: 'neurological' }) ? baseNeurologicalSchemaRefined : baseNeurologicalSchema.optional(),
    });
    const baseSectionBC = baseSectionB.merge(baseSectionC);
    const baseSectionD = z.object({
        psychosocial: isRequired('SectionD', { item: 'psychosocial' }) ? basePsychosocialSchemaRefined : basePsychosocialSchema.optional(),
        d0150: isRequired('SectionD', { item: 'd0150' }) ? baseD0150 : baseD0150.optional(),
        d0160: isRequired('SectionD', { item: 'd0160' }) ? z.string().nullable() : z.string().nullish(),
        d0700: isRequired('SectionD', { item: 'd0700' }) ? zStringRequired() : z.string().optional(),
    });
    const baseSectionE = z.object({
        m1740: isRequired('SectionE', { item: 'm1740' })
            ? z.array(z.string()).min(1, 'At least one is required')
            : z.array(z.string()).optional(),
        lastBehaviorOccurred: isRequired('SectionE', { item: 'm1740' }) ? z.iso.date().nullable() : z.iso.date().nullish(),
        m1745: isRequired('SectionE', { item: 'm1745' }) ? zStringRequired() : z.string().optional(),
    });
    const baseSectionDE = baseSectionD.merge(baseSectionE);
    const baseSectionF = z.object({
        m1100: isRequired('SectionF', { item: 'm1100' })
            ? baseM1100 // SOC / ROC: both keys required
            : baseM1100.partial().optional(),
        m2102: z.object({
            ADL: isRequired('SectionF', { item: 'm2102', subItem: 'ADL' }) ? zStringRequired() : z.string().optional(),
            MDCTN: isRequired('SectionF', { item: 'm2102', subItem: 'MDCTN' }) ? zStringRequired() : z.string().optional(),
            PRCDR: isRequired('SectionF', { item: 'm2102', subItem: 'PRCDR' }) ? zStringRequired() : z.string().optional(),
            SPRVSN: isRequired('SectionF', { item: 'm2102', subItem: 'SPRVSN' }) ? zStringRequired() : z.string().optional(),
        }),
    });
    const baseSectionG = z.object({
        musculoskeletal: isRequired('SectionG', { item: 'musculoskeletal' }) ? baseMusculoskeletalRefined : baseMusculoskeletal.optional(),
        m1800: isRequired('SectionG', { item: 'm1800' }) ? zStringRequired() : z.string().optional(),
        m1810: isRequired('SectionG', { item: 'm1810' }) ? zStringRequired() : z.string().optional(),
        m1820: isRequired('SectionG', { item: 'm1820' }) ? zStringRequired() : z.string().optional(),
        m1830: isRequired('SectionG', { item: 'm1830' }) ? zStringRequired() : z.string().optional(),
        m1840: isRequired('SectionG', { item: 'm1840' }) ? zStringRequired() : z.string().optional(),
        m1845: isRequired('SectionG', { item: 'm1845' }) ? zStringRequired() : z.string().optional(),
        m1850: isRequired('SectionG', { item: 'm1850' }) ? zStringRequired() : z.string().optional(),
        m1860: isRequired('SectionG', { item: 'm1860' }) ? zStringRequired() : z.string().optional(),
    });
    const baseSectionGG0130 = z.object({
        gg0130a1: isRequired('SectionGG', { item: 'gg0130', subItem: 'a1' }) ? zStringRequired() : z.string().optional(),
        gg0130a2: isRequired('SectionGG', { item: 'gg0130', subItem: 'a2' }) ? zStringRequired() : z.string().optional(),
        gg0130a3: isRequired('SectionGG', { item: 'gg0130', subItem: 'a3' }) ? zStringRequired() : z.string().optional(),
        gg0130a4: isRequired('SectionGG', { item: 'gg0130', subItem: 'a4' }) ? zStringRequired() : z.string().optional(),
        gg0130b1: isRequired('SectionGG', { item: 'gg0130', subItem: 'b1' }) ? zStringRequired() : z.string().optional(),
        gg0130b2: isRequired('SectionGG', { item: 'gg0130', subItem: 'b2' }) ? zStringRequired() : z.string().optional(),
        gg0130b3: isRequired('SectionGG', { item: 'gg0130', subItem: 'b3' }) ? zStringRequired() : z.string().optional(),
        gg0130b4: isRequired('SectionGG', { item: 'gg0130', subItem: 'b4' }) ? zStringRequired() : z.string().optional(),
        gg0130c1: isRequired('SectionGG', { item: 'gg0130', subItem: 'c1' }) ? zStringRequired() : z.string().optional(),
        gg0130c2: isRequired('SectionGG', { item: 'gg0130', subItem: 'c2' }) ? zStringRequired() : z.string().optional(),
        gg0130c3: isRequired('SectionGG', { item: 'gg0130', subItem: 'c3' }) ? zStringRequired() : z.string().optional(),
        gg0130c4: isRequired('SectionGG', { item: 'gg0130', subItem: 'c4' }) ? zStringRequired() : z.string().optional(),
        gg0130e1: isRequired('SectionGG', { item: 'gg0130', subItem: 'e1' }) ? zStringRequired() : z.string().optional(),
        gg0130e2: isRequired('SectionGG', { item: 'gg0130', subItem: 'e2' }) ? zStringRequired() : z.string().optional(),
        gg0130e3: isRequired('SectionGG', { item: 'gg0130', subItem: 'e3' }) ? zStringRequired() : z.string().optional(),
        gg0130e4: isRequired('SectionGG', { item: 'gg0130', subItem: 'e4' }) ? zStringRequired() : z.string().optional(),
        gg0130f1: isRequired('SectionGG', { item: 'gg0130', subItem: 'f1' }) ? zStringRequired() : z.string().optional(),
        gg0130f2: isRequired('SectionGG', { item: 'gg0130', subItem: 'f2' }) ? zStringRequired() : z.string().optional(),
        gg0130f3: isRequired('SectionGG', { item: 'gg0130', subItem: 'f3' }) ? zStringRequired() : z.string().optional(),
        gg0130f4: isRequired('SectionGG', { item: 'gg0130', subItem: 'f4' }) ? zStringRequired() : z.string().optional(),
        gg0130g1: isRequired('SectionGG', { item: 'gg0130', subItem: 'g1' }) ? zStringRequired() : z.string().optional(),
        gg0130g2: isRequired('SectionGG', { item: 'gg0130', subItem: 'g2' }) ? zStringRequired() : z.string().optional(),
        gg0130g3: isRequired('SectionGG', { item: 'gg0130', subItem: 'g3' }) ? zStringRequired() : z.string().optional(),
        gg0130g4: isRequired('SectionGG', { item: 'gg0130', subItem: 'g4' }) ? zStringRequired() : z.string().optional(),
        gg0130h1: isRequired('SectionGG', { item: 'gg0130', subItem: 'h1' }) ? zStringRequired() : z.string().optional(),
        gg0130h2: isRequired('SectionGG', { item: 'gg0130', subItem: 'h2' }) ? zStringRequired() : z.string().optional(),
        gg0130h3: isRequired('SectionGG', { item: 'gg0130', subItem: 'h3' }) ? zStringRequired() : z.string().optional(),
        gg0130h4: isRequired('SectionGG', { item: 'gg0130', subItem: 'h4' }) ? zStringRequired() : z.string().optional(),
    });
    const baseSectionGG0170 = z.object({
        gg0170a1: isRequired('SectionGG', { item: 'gg0170', subItem: 'a1' }) ? zStringRequired() : z.string().optional(),
        gg0170a2: isRequired('SectionGG', { item: 'gg0170', subItem: 'a2' }) ? zStringRequired() : z.string().optional(),
        gg0170a3: isRequired('SectionGG', { item: 'gg0170', subItem: 'a3' }) ? zStringRequired() : z.string().optional(),
        gg0170a4: isRequired('SectionGG', { item: 'gg0170', subItem: 'a4' }) ? zStringRequired() : z.string().optional(),
        gg0170b1: isRequired('SectionGG', { item: 'gg0170', subItem: 'b1' }) ? zStringRequired() : z.string().optional(),
        gg0170b2: isRequired('SectionGG', { item: 'gg0170', subItem: 'b2' }) ? zStringRequired() : z.string().optional(),
        gg0170b3: isRequired('SectionGG', { item: 'gg0170', subItem: 'b3' }) ? zStringRequired() : z.string().optional(),
        gg0170b4: isRequired('SectionGG', { item: 'gg0170', subItem: 'b4' }) ? zStringRequired() : z.string().optional(),
        gg0170c1: isRequired('SectionGG', { item: 'gg0170', subItem: 'c1' }) ? zStringRequired() : z.string().optional(),
        gg0170c2: isRequired('SectionGG', { item: 'gg0170', subItem: 'c2' }) ? zStringRequired() : z.string().optional(),
        gg0170c3: isRequired('SectionGG', { item: 'gg0170', subItem: 'c3' }) ? zStringRequired() : z.string().optional(),
        gg0170c4: isRequired('SectionGG', { item: 'gg0170', subItem: 'c4' }) ? zStringRequired() : z.string().optional(),
        gg0170d1: isRequired('SectionGG', { item: 'gg0170', subItem: 'd1' }) ? zStringRequired() : z.string().optional(),
        gg0170d2: isRequired('SectionGG', { item: 'gg0170', subItem: 'd2' }) ? zStringRequired() : z.string().optional(),
        gg0170d3: isRequired('SectionGG', { item: 'gg0170', subItem: 'd3' }) ? zStringRequired() : z.string().optional(),
        gg0170d4: isRequired('SectionGG', { item: 'gg0170', subItem: 'd4' }) ? zStringRequired() : z.string().optional(),
        gg0170e1: isRequired('SectionGG', { item: 'gg0170', subItem: 'e1' }) ? zStringRequired() : z.string().optional(),
        gg0170e2: isRequired('SectionGG', { item: 'gg0170', subItem: 'e2' }) ? zStringRequired() : z.string().optional(),
        gg0170e3: isRequired('SectionGG', { item: 'gg0170', subItem: 'e3' }) ? zStringRequired() : z.string().optional(),
        gg0170e4: isRequired('SectionGG', { item: 'gg0170', subItem: 'e4' }) ? zStringRequired() : z.string().optional(),
        gg0170f1: isRequired('SectionGG', { item: 'gg0170', subItem: 'f1' }) ? zStringRequired() : z.string().optional(),
        gg0170f2: isRequired('SectionGG', { item: 'gg0170', subItem: 'f2' }) ? zStringRequired() : z.string().optional(),
        gg0170f3: isRequired('SectionGG', { item: 'gg0170', subItem: 'f3' }) ? zStringRequired() : z.string().optional(),
        gg0170f4: isRequired('SectionGG', { item: 'gg0170', subItem: 'f4' }) ? zStringRequired() : z.string().optional(),
        gg0170g1: isRequired('SectionGG', { item: 'gg0170', subItem: 'g1' }) ? zStringRequired() : z.string().optional(),
        gg0170g2: isRequired('SectionGG', { item: 'gg0170', subItem: 'g2' }) ? zStringRequired() : z.string().optional(),
        gg0170g3: isRequired('SectionGG', { item: 'gg0170', subItem: 'g3' }) ? zStringRequired() : z.string().optional(),
        gg0170g4: isRequired('SectionGG', { item: 'gg0170', subItem: 'g4' }) ? zStringRequired() : z.string().optional(),
        gg0170i1: isRequired('SectionGG', { item: 'gg0170', subItem: 'i1' }) ? zStringRequired() : z.string().optional(),
        gg0170i2: isRequired('SectionGG', { item: 'gg0170', subItem: 'i2' }) ? zStringRequired() : z.string().optional(),
        gg0170i3: isRequired('SectionGG', { item: 'gg0170', subItem: 'i3' }) ? zStringRequired() : z.string().optional(),
        gg0170i4: isRequired('SectionGG', { item: 'gg0170', subItem: 'i4' }) ? zStringRequired() : z.string().optional(),
        gg0170j1: isRequired('SectionGG', { item: 'gg0170', subItem: 'j1' }) ? z.string().nullable() : z.string().nullish(),
        gg0170j2: isRequired('SectionGG', { item: 'gg0170', subItem: 'j2' }) ? z.string().nullable() : z.string().nullish(),
        gg0170j3: isRequired('SectionGG', { item: 'gg0170', subItem: 'j3' }) ? z.string().nullable() : z.string().nullish(),
        gg0170j4: isRequired('SectionGG', { item: 'gg0170', subItem: 'j4' }) ? z.string().nullable() : z.string().nullish(),
        gg0170k1: isRequired('SectionGG', { item: 'gg0170', subItem: 'k1' }) ? z.string().nullable() : z.string().nullish(),
        gg0170k2: isRequired('SectionGG', { item: 'gg0170', subItem: 'k2' }) ? z.string().nullable() : z.string().nullish(),
        gg0170k3: isRequired('SectionGG', { item: 'gg0170', subItem: 'k3' }) ? z.string().nullable() : z.string().nullish(),
        gg0170k4: isRequired('SectionGG', { item: 'gg0170', subItem: 'k4' }) ? z.string().nullable() : z.string().nullish(),
        gg0170l1: isRequired('SectionGG', { item: 'gg0170', subItem: 'l1' }) ? z.string().nullable() : z.string().nullish(),
        gg0170l2: isRequired('SectionGG', { item: 'gg0170', subItem: 'l2' }) ? z.string().nullable() : z.string().nullish(),
        gg0170l3: isRequired('SectionGG', { item: 'gg0170', subItem: 'l3' }) ? z.string().nullable() : z.string().nullish(),
        gg0170l4: isRequired('SectionGG', { item: 'gg0170', subItem: 'l4' }) ? z.string().nullable() : z.string().nullish(),
        gg0170m1: isRequired('SectionGG', { item: 'gg0170', subItem: 'm1' }) ? zStringRequired() : z.string().optional(),
        gg0170m2: isRequired('SectionGG', { item: 'gg0170', subItem: 'm2' }) ? zStringRequired() : z.string().optional(),
        gg0170m3: isRequired('SectionGG', { item: 'gg0170', subItem: 'm3' }) ? zStringRequired() : z.string().optional(),
        gg0170m4: isRequired('SectionGG', { item: 'gg0170', subItem: 'm4' }) ? zStringRequired() : z.string().optional(),
        gg0170n1: isRequired('SectionGG', { item: 'gg0170', subItem: 'n1' }) ? z.string().nullable() : z.string().nullish(),
        gg0170n2: isRequired('SectionGG', { item: 'gg0170', subItem: 'n2' }) ? z.string().nullable() : z.string().nullish(),
        gg0170n3: isRequired('SectionGG', { item: 'gg0170', subItem: 'n3' }) ? z.string().nullable() : z.string().nullish(),
        gg0170n4: isRequired('SectionGG', { item: 'gg0170', subItem: 'n4' }) ? z.string().nullable() : z.string().nullish(),
        gg0170o1: isRequired('SectionGG', { item: 'gg0170', subItem: 'o1' }) ? z.string().nullable() : z.string().nullish(),
        gg0170o2: isRequired('SectionGG', { item: 'gg0170', subItem: 'o2' }) ? z.string().nullable() : z.string().nullish(),
        gg0170o3: isRequired('SectionGG', { item: 'gg0170', subItem: 'o3' }) ? z.string().nullable() : z.string().nullish(),
        gg0170o4: isRequired('SectionGG', { item: 'gg0170', subItem: 'o4' }) ? z.string().nullable() : z.string().nullish(),
        gg0170p1: isRequired('SectionGG', { item: 'gg0170', subItem: 'p1' }) ? zStringRequired() : z.string().optional(),
        gg0170p2: isRequired('SectionGG', { item: 'gg0170', subItem: 'p2' }) ? zStringRequired() : z.string().optional(),
        gg0170p3: isRequired('SectionGG', { item: 'gg0170', subItem: 'p3' }) ? zStringRequired() : z.string().optional(),
        gg0170p4: isRequired('SectionGG', { item: 'gg0170', subItem: 'p4' }) ? zStringRequired() : z.string().optional(),
        gg0170q1: isRequired('SectionGG', { item: 'gg0170', subItem: 'q1' }) ? zStringRequired() : z.string().optional(),
        gg0170q2: isRequired('SectionGG', { item: 'gg0170', subItem: 'q2' }) ? zStringRequired() : z.string().optional(),
        gg0170q3: isRequired('SectionGG', { item: 'gg0170', subItem: 'q3' }) ? zStringRequired() : z.string().optional(),
        gg0170q4: isRequired('SectionGG', { item: 'gg0170', subItem: 'q4' }) ? zStringRequired() : z.string().optional(),
        gg0170r1: isRequired('SectionGG', { item: 'gg0170', subItem: 'r1' }) ? z.string().nullable() : z.string().nullish(),
        gg0170r2: isRequired('SectionGG', { item: 'gg0170', subItem: 'r2' }) ? z.string().nullable() : z.string().nullish(),
        gg0170r3: isRequired('SectionGG', { item: 'gg0170', subItem: 'r3' }) ? z.string().nullable() : z.string().nullish(),
        gg0170r4: isRequired('SectionGG', { item: 'gg0170', subItem: 'r4' }) ? z.string().nullable() : z.string().nullish(),
        gg0170rr1: isRequired('SectionGG', { item: 'gg0170', subItem: 'rr1' }) ? z.string().nullable() : z.string().nullish(),
        gg0170rr2: isRequired('SectionGG', { item: 'gg0170', subItem: 'rr2' }) ? z.string().nullable() : z.string().nullish(),
        gg0170rr3: isRequired('SectionGG', { item: 'gg0170', subItem: 'rr3' }) ? z.string().nullable() : z.string().nullish(),
        gg0170rr4: isRequired('SectionGG', { item: 'gg0170', subItem: 'rr4' }) ? z.string().nullable() : z.string().nullish(),
        gg0170s1: isRequired('SectionGG', { item: 'gg0170', subItem: 's1' }) ? z.string().nullable() : z.string().nullish(),
        gg0170s2: isRequired('SectionGG', { item: 'gg0170', subItem: 's2' }) ? z.string().nullable() : z.string().nullish(),
        gg0170s3: isRequired('SectionGG', { item: 'gg0170', subItem: 's3' }) ? z.string().nullable() : z.string().nullish(),
        gg0170s4: isRequired('SectionGG', { item: 'gg0170', subItem: 's4' }) ? z.string().nullable() : z.string().nullish(),
        gg0170ss1: isRequired('SectionGG', { item: 'gg0170', subItem: 'ss1' }) ? z.string().nullable() : z.string().nullish(),
        gg0170ss2: isRequired('SectionGG', { item: 'gg0170', subItem: 'ss2' }) ? z.string().nullable() : z.string().nullish(),
        gg0170ss3: isRequired('SectionGG', { item: 'gg0170', subItem: 'ss3' }) ? z.string().nullable() : z.string().nullish(),
        gg0170ss4: isRequired('SectionGG', { item: 'gg0170', subItem: 'ss4' }) ? z.string().nullable() : z.string().nullish(),
    });
    const processedGG0170 = refineSchema(baseSectionGG0170, (input, ctx) => {
        sectionGGSkipPatterns({ input, ctx, version, isSection: true });
    });
    const baseSectionGG = z.object({
        gg0100: isRequired('SectionGG', { item: 'gg0100' }) ? baseGG0100 : baseGG0100.optional(),
        gg0110: isRequired('SectionGG', { item: 'gg0110' }) ? z.array(z.string()) : z.array(z.string()).optional(),
        gg0130: baseSectionGG0130,
        gg0170: processedGG0170,
    });
    const baseSectionH = z.object({
        m1600: isRequired('SectionH', { item: 'm1600' }) ? zStringRequired() : z.string().optional(),
        m1610: isRequired('SectionH', { item: 'm1610' }) ? zStringRequired() : z.string().optional(),
        genitourinary: isRequired('SectionH', { item: 'genitourinary' })
            ? baseGenitourinarySchemaRefined
            : baseGenitourinarySchema.optional(),
        m1620: isRequired('SectionH', { item: 'm1620' }) ? zStringRequired() : z.string().optional(),
        m1630: isRequired('SectionH', { item: 'm1630' }) ? zStringRequired() : z.string().optional(),
        gastrointestinal: isRequired('SectionH', { item: 'gastrointestinal' })
            ? baseGastrointestinalSchemaRefined
            : baseGastrointestinalSchema.optional(),
    });
    const baseSectionI = z
        .object({
        m1021: isRequired('SectionI', { item: 'm1021' }) ? baseDiagnosis : baseDiagnosis.optional(),
        m1023: isRequired('SectionI', { item: 'm1023' })
            ? z.array(baseDiagnosis).superRefine((input, ctx) => {
                // Check for duplicates within m1023
                const seenCodes = new Set();
                input.forEach(({ code, severity }, index) => {
                    if (!code) {
                        return;
                    }
                    if (seenCodes.has(code)) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `Duplicate diagnosis code: ${code}`,
                            path: [index, 'code'],
                        });
                    }
                    else {
                        seenCodes.add(code);
                    }
                    if (!severity) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `-4991 Severity required`,
                            path: [index, 'severity'],
                        });
                    }
                });
            })
            : z.array(baseDiagnosis).optional(),
        m1028: isRequired('SectionI', { item: 'm1028' }) ? processedM1028 : z.array(z.string()).optional(),
    })
        .superRefine((data, ctx) => {
        // Check for duplicate diagnosis codes between m1021 and m1023
        if (data.m1021?.code && data.m1023) {
            const primaryCode = data.m1021.code;
            data.m1023.forEach(({ code }, index) => {
                if (code && code === primaryCode) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Duplicate diagnosis code: ${code}`,
                        path: ['m1023', index, 'code'],
                    });
                }
            });
        }
    });
    const baseSectionJ = z.object({
        painAssessment: isRequired('SectionJ', { item: 'painAssessment' }) ? basePainAssessmentSchema : basePainAssessmentSchema.optional(),
        j0510: isRequired('SectionJ', { item: 'j0510' }) ? zStringRequired() : z.string().optional(),
        j0520: isRequired('SectionJ', { item: 'j0520' }) ? z.string().nullable() : z.string().nullish(),
        j0530: isRequired('SectionJ', { item: 'j0530' }) ? z.string().nullable() : z.string().nullish(),
        j1800: isRequired('SectionJ', { item: 'j1800' }) ? zStringRequired() : z.string().optional(),
        j1900: isRequired('SectionJ', { item: 'j1900' }) ? baseJ1900 : baseJ1900.optional(),
        m1033: isRequired('SectionJ', { item: 'm1033' }) ? processedM1033 : z.array(z.string()).optional(),
        m1400: isRequired('SectionJ', { item: 'm1400' }) ? zStringRequired() : z.string().optional(),
    });
    const baseSectionJProcessed = refineSchema(baseSectionJ, (input, ctx) => {
        sectionJConsistency({ input, ctx, version, isSection: true });
        sectionJSkipPatterns({ input, ctx, version, isSection: true });
    });
    const baseSectionKK0520 = z.object({
        a1: isRequired('SectionK', { item: 'k0520', subItem: 'a1' }) ? z.boolean() : z.boolean().optional(),
        b1: isRequired('SectionK', { item: 'k0520', subItem: 'b1' }) ? z.boolean() : z.boolean().optional(),
        c1: isRequired('SectionK', { item: 'k0520', subItem: 'c1' }) ? z.boolean() : z.boolean().optional(),
        d1: isRequired('SectionK', { item: 'k0520', subItem: 'd1' }) ? z.boolean() : z.boolean().optional(),
        z1: isRequired('SectionK', { item: 'k0520', subItem: 'z1' }) ? z.boolean() : z.boolean().optional(),
        a4: isRequired('SectionK', { item: 'k0520', subItem: 'a4' }) ? z.boolean() : z.boolean().optional(),
        b4: isRequired('SectionK', { item: 'k0520', subItem: 'b4' }) ? z.boolean() : z.boolean().optional(),
        c4: isRequired('SectionK', { item: 'k0520', subItem: 'c4' }) ? z.boolean() : z.boolean().optional(),
        d4: isRequired('SectionK', { item: 'k0520', subItem: 'd4' }) ? z.boolean() : z.boolean().optional(),
        z4: isRequired('SectionK', { item: 'k0520', subItem: 'z4' }) ? z.boolean() : z.boolean().optional(),
        a5: isRequired('SectionK', { item: 'k0520', subItem: 'a5' }) ? z.boolean() : z.boolean().optional(),
        b5: isRequired('SectionK', { item: 'k0520', subItem: 'b5' }) ? z.boolean() : z.boolean().optional(),
        c5: isRequired('SectionK', { item: 'k0520', subItem: 'c5' }) ? z.boolean() : z.boolean().optional(),
        d5: isRequired('SectionK', { item: 'k0520', subItem: 'd5' }) ? z.boolean() : z.boolean().optional(),
        z5: isRequired('SectionK', { item: 'k0520', subItem: 'z5' }) ? z.boolean() : z.boolean().optional(),
    });
    const processedSectionKK0520 = refineSchema(baseSectionKK0520, (input, ctx) => {
        sectionKConsistency({ input, ctx, field: 'k0520' });
    });
    const baseSectionK = z.object({
        m1060: isRequired('SectionK', { item: 'm1060' }) ? baseM1060Refined : baseM1060.nullish(),
        weightClinical: isRequired('SectionK', { item: 'weightClinical' }) ? baseWeightClinical : baseWeightClinical.optional(),
        k0520: isRequired('SectionK', { item: 'k0520' }) ? processedSectionKK0520 : baseSectionKK0520.optional(),
        k0520Clinical: isRequired('SectionK', { item: 'k0520Clinical' }) ? baseK0520Clinical : baseK0520Clinical.optional(),
        oralIntakeClinical: isRequired('SectionK', { item: 'oralIntakeClinical' })
            ? baseOralIntakeClinical
            : baseOralIntakeClinical.optional(),
        m1870: isRequired('SectionK', { item: 'm1870' }) ? zStringRequired() : z.string().optional(),
        eatingSupport: isRequired('SectionK', { item: 'eatingSupport' }) ? baseEatingSupport : baseEatingSupport.optional(),
        pocNutrition: isRequired('SectionK', { item: 'pocNutrition' }) ? basePocNutrition : basePocNutrition.optional(),
    });
    const baseM1311 = z.object({
        m1311a1: isRequired('SectionM', { item: 'm1311', subItem: 'a1' }) ? z.string().nullable() : z.string().nullish(),
        m1311a2: isRequired('SectionM', { item: 'm1311', subItem: 'a2' }) ? z.string().nullable() : z.string().nullish(),
        m1311b1: isRequired('SectionM', { item: 'm1311', subItem: 'b1' }) ? z.string().nullable() : z.string().nullish(),
        m1311b2: isRequired('SectionM', { item: 'm1311', subItem: 'b2' }) ? z.string().nullable() : z.string().nullish(),
        m1311c1: isRequired('SectionM', { item: 'm1311', subItem: 'c1' }) ? z.string().nullable() : z.string().nullish(),
        m1311c2: isRequired('SectionM', { item: 'm1311', subItem: 'c2' }) ? z.string().nullable() : z.string().nullish(),
        m1311d1: isRequired('SectionM', { item: 'm1311', subItem: 'd1' }) ? z.string().nullable() : z.string().nullish(),
        m1311d2: isRequired('SectionM', { item: 'm1311', subItem: 'd2' }) ? z.string().nullable() : z.string().nullish(),
        m1311e1: isRequired('SectionM', { item: 'm1311', subItem: 'e1' }) ? z.string().nullable() : z.string().nullish(),
        m1311e2: isRequired('SectionM', { item: 'm1311', subItem: 'e2' }) ? z.string().nullable() : z.string().nullish(),
        m1311f1: isRequired('SectionM', { item: 'm1311', subItem: 'f1' }) ? z.string().nullable() : z.string().nullish(),
        m1311f2: isRequired('SectionM', { item: 'm1311', subItem: 'f2' }) ? z.string().nullable() : z.string().nullish(),
    });
    const baseSectionM = z.object({
        m1306: isRequired('SectionM', { item: 'm1306' }) ? zStringRequired() : z.string().optional(),
        m1307: isRequired('SectionM', { item: 'm1307' }) ? z.string().nullable() : z.string().nullish(),
        m1307DT: isRequired('SectionM', { item: 'm1307' }) ? z.iso.date().nullable() : z.iso.date().nullish(),
        m1311: isRequired('SectionM', { item: 'm1311' }) ? baseM1311 : baseM1311.optional(),
        m1322: isRequired('SectionM', { item: 'm1322' }) ? zStringRequired() : z.string().optional(),
        m1324: isRequired('SectionM', { item: 'm1324' }) ? zStringRequired() : z.string().optional(),
        multiplePressureUlcer: isRequired('SectionM', { item: 'multiplePressureUlcer' }) ? z.string().nullable() : z.string().nullish(),
        m1330: isRequired('SectionM', { item: 'm1330' }) ? zStringRequired() : z.string().optional(),
        m1332: isRequired('SectionM', { item: 'm1332' }) ? z.string().nullable() : z.string().nullish(),
        m1334: isRequired('SectionM', { item: 'm1334' }) ? z.string().nullable() : z.string().nullish(),
        multipleStasisUlcer: isRequired('SectionM', { item: 'multipleStasisUlcer' }) ? z.string().nullable() : z.string().nullish(),
        m1340: isRequired('SectionM', { item: 'm1340' }) ? zStringRequired() : z.string().optional(),
        m1342: isRequired('SectionM', { item: 'm1342' }) ? z.string().nullable() : z.string().nullish(),
        multipleWound: isRequired('SectionM', { item: 'multipleWound' }) ? z.string().nullable() : z.string().nullish(),
        integumentary: isRequired('SectionM', { item: 'integumentary' }) ? baseIntegumentaryRefined : baseIntegumentary.optional(),
    });
    const baseSectionN = z.object({
        n0415: isRequired('SectionN', { item: 'n0415' }) ? baseN0415 : baseN0415.optional(),
        m2001: isRequired('SectionN', { item: 'm2001' }) ? zStringRequired() : z.string().optional(),
        m2001Specify: z.string().nullish(),
        actionsTaken: isRequired('SectionN', { item: 'actionsTaken' }) ? baseActionsTaken : baseActionsTaken.optional(),
        m2003: isRequired('SectionN', { item: 'm2003' }) ? z.string().nullable() : z.string().nullish(),
        m2005: isRequired('SectionN', { item: 'm2005' }) ? zStringRequired() : z.string().optional(),
        m2010: isRequired('SectionN', { item: 'm2010' }) ? z.string().nullable() : z.string().nullish(),
        m2020: isRequired('SectionN', { item: 'm2020' }) ? z.string().nullable() : z.string().nullish(),
        m2030: isRequired('SectionN', { item: 'm2030' }) ? z.string().nullable() : z.string().nullish(),
        abilityAssessment: isRequired('SectionN', { item: 'abilityAssessment' }) ? baseAbilityAssessment : baseAbilityAssessment.optional(),
    });
    const baseSectionNProcessed = refineSchema(baseSectionN, (input, ctx) => {
        sectionNConsistency({ input, ctx, version, isSection: true });
        sectionNSkipPatterns({ input, ctx, version, isSection: true });
    });
    const baseSectionO = z.object({
        o0110: isRequired('SectionO', { item: 'o0110' }) ? baseO0110 : baseO0110.optional(),
        o0350: isRequired('SectionO', { item: 'o0350' }) ? zStringRequired() : z.string().optional(),
        m1041: isRequired('SectionO', { item: 'm1041' }) ? zStringRequired() : z.string().optional(),
        m1046: isRequired('SectionO', { item: 'm1046' }) ? z.string().nullable() : z.string().nullish(),
        m2200: isRequired('SectionO', { item: 'm2200' }) ? baseM2200Refined : baseM2200.optional(),
    });
    const baseSectionOProcessed = refineSchema(baseSectionO, (input, ctx) => {
        sectionOSkipPatterns({ input, ctx, version, isSection: true });
    });
    const baseVitals = z.object({
        bloodPressure: isRequired('Vitals', { item: 'bloodPressure' }) ? baseBloodPressureRefined : baseBloodPressure.optional(),
        pulse: isRequired('Vitals', { item: 'pulse' }) ? basePulseRefined : basePulse.optional(),
        respiration: isRequired('Vitals', { item: 'respiration' }) ? baseRespirationRefined : baseRespiration.optional(),
        temperature: isRequired('Vitals', { item: 'temperature' }) ? baseTemperatureRefined : baseTemperature.optional(),
        o2: isRequired('Vitals', { item: 'o2' }) ? baseO2Refined : baseO2.optional(),
        pain: isRequired('Vitals', { item: 'pain' }) ? basePainRefined : basePain.optional(),
        bloodSugar: isRequired('Vitals', { item: 'bloodSugar' }) ? baseBloodSugarRefined : baseBloodSugar.optional(),
    });
    const baseSectionQ = z.object({
        m2401: isRequired('SectionQ', { item: 'm2401' })
            ? z.array(z.string()).min(1, 'At least one is required')
            : z.array(z.string()).optional(),
    });
    const baseRespiratory = z.object({
        smokingHistory: isRequired('Respiratory', { item: 'smokingHistory' })
            ? baseSmokingHistorySchema
            : baseSmokingHistorySchema.optional(),
        breathingPattern: isRequired('Respiratory', { item: 'breathingPattern' })
            ? baseBreathingPatternSchema
            : baseBreathingPatternSchema.optional(),
        lungAuscultation: isRequired('Respiratory', { item: 'lungAuscultation' })
            ? baseLungAuscultationSchemaRefined
            : baseLungAuscultationSchema.optional(),
        cough: isRequired('Respiratory', { item: 'cough' }) ? baseCoughSchema : baseCoughSchema.optional(),
        cpapBipap: isRequired('Respiratory', { item: 'cpapBipap' }) ? baseCpapBipapSchema : baseCpapBipapSchema.optional(),
        mechanicalVentilator: isRequired('Respiratory', { item: 'mechanicalVentilator' })
            ? baseMechanicalVentilatorSchema
            : baseMechanicalVentilatorSchema.optional(),
        tracheostomy: isRequired('Respiratory', { item: 'tracheostomy' }) ? baseTracheostomySchema : baseTracheostomySchema.optional(),
        treatments: isRequired('Respiratory', { item: 'treatments' })
            ? baseRespiratoryTreatmentsSchema
            : baseRespiratoryTreatmentsSchema.optional(),
    });
    const baseCardiovascular = z.object({
        cardiovascular: isRequired('Cardiovascular', { item: 'cardiovascular' })
            ? baseCardiovascularSchemaRefined
            : baseCardiovascularSchema.optional(),
    });
    const baseOstomy = z.object({
        ostomy: isRequired('Ostomy', { item: 'ostomy' }) ? baseOstomySchemaRefined : baseOstomySchema.optional(),
    });
    const endocrineSchema = ['roc', 'rct', 'dsch'].includes(timepoint)
        ? baseEndocrineSchema.extend({ changeFromLastVisit: zStringRequired() })
        : baseEndocrineSchema;
    const baseEndocrine = z.object({
        endocrine: isRequired('Endocrine', { item: 'endocrine' }) ? endocrineSchema : endocrineSchema.optional(),
    });
    const baseHealthManagement = z.object({
        healthManagement: isRequired('HealthManagement', { item: 'healthManagement' })
            ? baseHealthManagementSchema
            : baseHealthManagementSchema.optional(),
    });
    const baseLabDraw = z.object({
        labdraw: isRequired('Labdraw', { item: 'labdraw' }) ? baseLabDrawSchema : baseLabDrawSchema.optional(),
    });
    const baseNarrative = z.object({
        interventions: isRequired('Narrative', { item: 'interventions' }) ? z.string().trim().min(0, 'required') : z.string().optional(),
        responseToCareAndProgress: isRequired('Narrative', { item: 'responseToCareAndProgress' })
            ? z.string().min(0, 'required')
            : z.string().optional(),
        visitNarrative: isRequired('Narrative', { item: 'visitNarrative' }) ? z.string().trim().min(0, 'required') : z.string().optional(),
    });
    const baseCoordinationOfCare = z.object({
        physicianNotified: z.array(z.string()).optional(),
        clinicianName: z.string().optional(),
        collaboratorName: z.string().optional(),
        nextVisitPlans: isRequired('CoordinationOfCare', { item: 'nextVisitPlans' }) ? zStringRequired() : z.string().optional(),
        coordinationDiscipline: z.array(z.string()).optional(),
        coordinationName: z
            .object({
            md: z.string().optional(),
            np: z.string().optional(),
            pa: z.string().optional(),
            sn: z.string().optional(),
            lpn: z.string().optional(),
            pt: z.string().optional(),
            ot: z.string().optional(),
            slp: z.string().optional(),
            hha: z.string().optional(),
        })
            .optional(),
    });
    const baseSchema = z.object({
        SectionA: isRequired('SectionA', { isSection: true }) ? baseSectionAProccessed : baseSectionAProccessed.optional(),
        Vitals: isRequired('Vitals', { isSection: true }) ? baseVitals : baseVitals.optional(),
        SectionJ: isRequired('SectionJ', { isSection: true }) ? baseSectionJProcessed : baseSectionJProcessed.optional(),
        SectionK: isRequired('SectionK', { isSection: true }) ? baseSectionK : baseSectionK.optional(),
        SectionBC: isRequired('SectionB', { isSection: true }) || isRequired('SectionC', { isSection: true })
            ? baseSectionBC
            : baseSectionBC.optional(),
        SectionDE: isRequired('SectionD', { isSection: true }) || isRequired('SectionE', { isSection: true })
            ? baseSectionDE
            : baseSectionDE.optional(),
        SectionF: isRequired('SectionF', { isSection: true }) ? baseSectionF : baseSectionF.optional(),
        SectionG: isRequired('SectionG', { isSection: true }) ? baseSectionG : baseSectionG.optional(),
        SectionGG: isRequired('SectionGG', { isSection: true }) ? baseSectionGG : baseSectionGG.optional(),
        SectionH: isRequired('SectionH', { isSection: true }) ? baseSectionH : baseSectionH.optional(),
        SectionI: isRequired('SectionI', { isSection: true }) ? baseSectionI : baseSectionI.optional(),
        SectionM: isRequired('SectionM', { isSection: true }) ? baseSectionM : baseSectionM.optional(),
        Medication: isRequired('Medication', { isSection: true }) ? z.array(z.string()).nullable() : z.array(z.string()).nullish(),
        SectionN: isRequired('SectionN', { isSection: true }) ? baseSectionNProcessed : baseSectionNProcessed.optional(),
        SectionO: isRequired('SectionO', { isSection: true }) ? baseSectionOProcessed : baseSectionOProcessed.optional(),
        SectionQ: isRequired('SectionQ', { isSection: true }) ? baseSectionQ : baseSectionQ.optional(),
        Respiratory: isRequired('Respiratory', { isSection: true }) ? baseRespiratory : baseRespiratory.optional(),
        Cardiovascular: isRequired('Cardiovascular', { isSection: true }) ? baseCardiovascular : baseCardiovascular.optional(),
        Endocrine: isRequired('Endocrine', { isSection: true }) ? baseEndocrine : baseEndocrine.optional(),
        HealthManagement: isRequired('HealthManagement', { isSection: true }) ? baseHealthManagement : baseHealthManagement.optional(),
        Supplies: isRequired('Supplies', { isSection: true }) ? z.array(z.string()).nullable() : z.array(z.string()).nullish(),
        PlanOfCare: isRequired('PlanOfCare', { isSection: true }) ? z.boolean().nullable() : z.array(z.string()).nullish(),
        Homebound: isRequired('Homebound', { isSection: true }) ? baseHomeboundRefined : baseHomebound.optional(),
        Labdraw: isRequired('Labdraw', { isSection: true }) ? baseLabDraw : baseLabDraw.optional(),
        Ostomy: isRequired('Ostomy', { isSection: true }) ? baseOstomy : baseOstomy.optional(),
        CoordinationOfCare: isRequired('CoordinationOfCare', { isSection: true })
            ? baseCoordinationOfCare
            : baseCoordinationOfCare.optional(),
        Narrative: isRequired('Narrative', { isSection: true }) ? baseNarrative : baseNarrative.optional(),
    });
    return baseSchema;
};
export const getOasisValidationSchema = (version, timepoint) => {
    const schema = getBaseOasisValidationSchema(version, timepoint);
    const processedSchema = refineSchema(schema, (input, ctx) => {
        sectionAConsistency({ input, ctx, version });
        sectionCConsistency({ input, ctx, version });
        sectionCSkipPatterns({ input, ctx, version });
        sectionDConsistency({ input, ctx, version });
        sectionEConsistency({ input, ctx, version });
        sectionFConsistency({ input, ctx, version });
        sectionGGConsistency({ input, ctx, version });
        sectionHConsistency({ input, ctx, version });
        sectionIConsistency({ input, ctx, version });
        sectionMConsistency({ input, ctx, version });
        sectionMSkipPatterns({ input, ctx, version });
        sectionNConsistency({ input, ctx, version });
        sectionOConsistency({ input, ctx, version });
        // sectionBConsistency
        // sectionBSkipPatterns
        // ...
    });
    return z.object({
        id: z.string(),
        data: processedSchema,
        signedUserId: zStringRequired(),
        signedUserDate: zStringRequired(),
    });
};
/**
 *
 * @param schema
 * @param sectionKey
 * @param sectionItemKey
 * @returns filters validation schema to return an array of required oasis items
 */
export const getRequiredFields = (schema, sectionKey, sectionItemKey) => {
    let section = !sectionKey ? schema : !sectionItemKey ? schema.shape[sectionKey] : schema.shape[sectionKey].shape[sectionItemKey];
    // Optional wrappers: unwrap so we can read the object shape
    if (section && typeof section.unwrap === 'function' && !section.shape) {
        section = section.unwrap();
    }
    const shape = section?.shape;
    if (!shape) {
        return;
    }
    return Object.keys(shape).filter((fieldKey) => {
        const field = shape[fieldKey];
        return !field.isOptional();
    });
};
/**
 * Returns the alias name for an OASIS item in a given section and version.
 * Used by frontend to look up the correct component for a versioned item.
 *
 * @param section - The OASIS section (e.g. 'SectionA')
 * @param item - The item key within the section (e.g. 'm0040')
 * @param version - The OASIS version (e.g. 'e1')
 * @returns The alias string if the item exists for the given version, otherwise undefined
 */
export function getItemAlias(section, item, version) {
    const itemConfig = oasis[section]?.[item];
    if (itemConfig) {
        const entry = resolveVersionEntry(itemConfig, version);
        return entry?.alias;
    }
    // When the schema key (e.g. 'a1110a') doesn't match the versioned config key (e.g. 'a1110'),
    // scan through items in the section and find one whose resolved alias matches the schema key.
    const sectionConfig = oasis[section];
    if (sectionConfig) {
        for (const versionMap of Object.values(sectionConfig)) {
            const entry = resolveVersionEntry(versionMap, version);
            if (entry?.alias === item) {
                return entry.alias;
            }
        }
    }
    return undefined;
}
export function getFieldTags(section, item, version, subItem) {
    // Direct lookup: config key matches caller's item (e.g. 'a1110')
    const directEntry = resolveVersionEntry(oasis[section]?.[item] ?? {}, version);
    if (directEntry) {
        if (subItem && directEntry.fields) {
            return directEntry.fields[subItem]?.tags ?? [];
        }
        return directEntry.tags ?? [];
    }
    // Alias fallback: caller passed a schema alias key (e.g. 'a1110a') that doesn't match the
    // config key ('a1110') — scan the section for an entry whose resolved alias matches.
    const sectionConfig = oasis[section];
    if (sectionConfig) {
        for (const versionMap of Object.values(sectionConfig)) {
            const entry = resolveVersionEntry(versionMap, version);
            if (entry?.alias === item) {
                if (subItem && entry.fields) {
                    return entry.fields[subItem]?.tags ?? [];
                }
                return entry.tags ?? [];
            }
        }
    }
    return [];
}
/**
 * Returns all item aliases for a given section and version,
 * filtered to those active for the specified timepoint.
 *
 * @param section - The OASIS section
 * @param version - The OASIS version
 * @param timepoint - The OASIS timepoint
 * @returns Array of { item, alias } for active items
 */
export function getSectionItems(section, version, timepoint) {
    const sectionConfig = oasis[section];
    if (!sectionConfig) {
        return [];
    }
    const result = [];
    for (const [item, versionMap] of Object.entries(sectionConfig)) {
        const entry = resolveVersionEntry(versionMap, version);
        if (entry && (entry.timepoint.includes(timepoint) || entry.timepoint.includes('*'))) {
            result.push({ item, alias: entry.alias });
        }
    }
    return result;
}
